import React, { useEffect, useMemo, useState } from "react";
import * as ss from "simple-statistics";
import { sortBy, uniq } from "lodash";
import { Area, AreaChart, Label, LabelList, Tooltip, XAxis, YAxis } from "recharts";

import { api, getAuthorizationHeader, getServerUrl, useApi } from "../../services/api";
import { formatDateTime, formatDay, getDateTime } from "../../services/date";

import { Account, AccountPerformance, IBKROrder, Ledger, Orders, PortfolioSummarySimple, } from "../../services/ibkr";
import { TableInline, TableMail } from "../components/Table";
import { Accordion, colors, getUsd, Loading, Percent, USD } from "../components";

import type { ScriptName } from "../../services/script";
import type { AppConfiguration, Trade as TradeType } from "../../../types";



export default function DashboardIBKR() {
    const [authenticated, setAuthenticated] = useState(false);

    return <div className="padding">
        <h1>IBKR Dashboard</h1>
        <Accordion title="Konfigurace"><Configuration /></Accordion>
        <Accordion title="Skripty"><Scripts /></Accordion>
        <ConnectionStatus setAuthenticated={setAuthenticated} />
        {authenticated && <Dashboard />}
        <div style={{ marginTop: "20px" }} />
        <Accordion title="Logy"><Logs /></Accordion>
    </div>
}

export function ConnectionStatus({ setAuthenticated }: { setAuthenticated: (authenticated: boolean) => void }) {
    const [authStatus, error, isLoading, , reloadAuthStatus] = useApi.getIBKRAuthStatus({});

    const isLoggedOff = error || authStatus?.authenticated === false;

    useEffect(() => {
        if (isLoading)
            return;

        if (authStatus && authStatus.authenticated)
            setAuthenticated(true);
        else
            setAuthenticated(false);
    }, [authStatus, isLoading]);

    function handleReloadStatus() {
        reloadAuthStatus();
    }

    async function handleLogout() {
        await api.addIBKRLogout({});
        reloadAuthStatus();
    }

    return <>
        <div className="buttons">
            <button onClick={handleReloadStatus}>Přenačíst status</button>
            <button onClick={handleLogout}>Odhlásit se z IBKR Client Portal GW</button>
        </div>
        {isLoading && <Loading />}
        {error && <p className="error">Auth Status Error: {error}</p>}
        {authStatus && <>
            <p>
                Connected: {authStatus.connected ? "🟢" : "🔴"} {" "}
                Authenticated: {authStatus.authenticated ? "🟢" : "🔴"}
            </p>
            {error && <p className="error">Auth Status Error: {error}</p>}
        </>}
        {isLoggedOff && <Login onLogged={reloadAuthStatus} />}
    </>
}

export function Login({ onLogged }: { onLogged: (logged: boolean) => void }) {
    const [imgSrc, setImageSrc] = useState("");
    const [error, setError] = useState("");


    useEffect(() => {
        async function fetchImage() {
            try {
                const response = await fetch(`${getServerUrl()}/ibkr-login.webp?${Date.now()}`, {
                    method: "GET",
                    headers: {
                        'Authorization': getAuthorizationHeader(),
                    }
                });

                if (!response.ok) {
                    throw new Error('Nepodařilo se načíst obrázek');
                }

                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                setImageSrc(objectUrl);
            } catch (error) {
                console.error('Chyba při načítání obrázku:', error);
            }
        };

        const interval = setInterval(fetchImage, 2000);

        return () => clearInterval(interval);
    }, []);

    async function handleLogin() {
        setError("");
        const [, error] = await api.addIBKRLogin({});
        if (error) {
            setError(error);
            return;
        }
        onLogged(true);
    }

    return <Accordion title="Přihlášení do IBKR Client Portal GW" defaultOpen>
        {error && <p className="error">{error}</p>}
        {imgSrc && <img src={imgSrc} />}
        <br />
        <button onClick={handleLogin}>
            Přihlásit se do IBKR Client Portal GW
        </button>
    </Accordion>;
}

export function Dashboard() {

    return <>
        <Accordion title="Účet" defaultOpen><Account /></Accordion>
        <Accordion title="Graf výkonu"><Performance /></Accordion>
        <Accordion title="Graf vývoje k 70 000 USD (Bar Chart)" defaultOpen><PerformanceBarChart70k /></Accordion>
        <Accordion title="Účetní kniha"><Ledger /></Accordion>
        <Accordion title="Otevřené pozice" defaultOpen><Positions /></Accordion>
        <Accordion title="Uzavřené obchody"><ClosedTrades /></Accordion>
        <Accordion title="Obchody"><Trades /></Accordion>
        <Accordion title="Objednávky"><Orders /></Accordion>
        <Accordion title="Live Objednávky"><LiveOrders /></Accordion>
    </>;
}

export function Scripts() {
    const [ss, , isLoading, , reload] = useApi.getScriptStatus({});

    async function handleRun(scriptName: ScriptName) {
        await api.addRunScript({ scriptName });
        reload();
    }

    async function handleStop(scriptName: ScriptName) {
        await api.addStopScript({ scriptName, message: "Manual stop from dashboard" });
        reload();
    }

    return <>
        {isLoading && <Loading />}
        {ss && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <table style={{ textAlign: "left" }}>
                <thead>
                    <tr>
                        <th>Název scriptu</th>
                        <th>Naposledy spuštěn</th>
                        <th>Stav</th>
                        <th>Akce</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(ss).map(([key, v]) => <tr key={key}>
                        <td><b>{key}</b></td>
                        <td>{v.lastRunTime ? formatDateTime(v.lastRunTime) : " - "}</td>
                        <td>{v.status === "running" ? "🟢 " : "🔴 "}{v.message}</td>
                        <td>
                            <button onClick={() => handleRun(key as ScriptName)}>Spustit</button>
                            <button onClick={() => handleStop(key as ScriptName)}>Zastavit</button>
                        </td>
                    </tr>)}
                </tbody>
            </table>
        </>}
    </>;
}

export function Account() {
    const [account, , isLoading, , reload] = useApi.getIBKRAccount({});
    const [portfolio, , , , reloadPortfolio] = useApi.getIBKRPortfolioSummary({});

    function handleReload() {
        reload();
        reloadPortfolio();
    }

    return <>
        {isLoading && <Loading />}
        {account && portfolio && <>
            <button className="right" onClick={handleReload}>Přenačíst</button>
            <table style={{ textAlign: "left" }}>
                <tbody>
                    <tr><td style={{ width: "250px" }}>Účet</td><td>{account.selectedAccount}</td></tr>
                    <tr><td>Typ</td><td>{account.isPaper ? "🟢 Paper Trading" : "🔴 Live Trading"}</td></tr>
                    <tr><td>Aktualizováno</td><td>{formatDateTime(portfolio.dateTime)}</td></tr>
                    <tr><td>Celková hodnota účtu</td><td><USD value={portfolio.netliquidation} /></td></tr>
                    <tr><td>Nákupní síla</td><td><USD value={portfolio.buyingpower} /></td></tr>
                    <tr><td title="Nesmí na 0 - margin call!">Přebytečná likvidita ❓</td><td><USD value={portfolio.excessliquidity} /></td></tr>
                    <tr><td>Dostupné prostředky</td><td><USD value={portfolio.availablefunds} /></td></tr>
                    <tr><td>Celkem investováno</td><td><USD value={portfolio.grosspositionvalue} /></td></tr>
                    <tr><td title="Záporná hodnota je půjčka">Celková hotovost ❓</td><td><USD value={portfolio.totalcashvalue} /></td></tr>
                    <tr><td>Požadovaná udržovací marže</td><td><USD value={portfolio.maintmarginreq} /></td></tr>
                    <tr><td>Páka</td><td>{portfolio["leverage-s"]}</td></tr>
                    <tr><td>Polštář</td><td><Percent value={Number(portfolio.cushion)} /></td></tr>
                </tbody>
            </table>
        </>}
    </>
}

export function Configuration() {
    const [configuration, , isLoading, , reload] = useApi.getAppConfiguration({});

    const [orderPriceLimit, setOrderPriceLimit] = useState(0);
    const [spreadLimit, setSpreadLimit] = useState(0);

    useEffect(() => {
        if (!configuration)
            return;

        setOrderPriceLimit(configuration.orderPriceLimit);
        setSpreadLimit(configuration.spreadLimitPercent * 100);
    }, [configuration]);


    async function handleChange(key: keyof AppConfiguration, value: any) {
        if (!configuration) return;

        await api.updateAppConfiguration({ ...configuration, [key]: value });
        reload();
    }

    return <>
        {isLoading && <Loading />}
        {configuration && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <table style={{ textAlign: "left" }}>
                <tbody>
                    <tr><td style={{ width: "200px" }}>API (hard stop)</td><td><Switch value={configuration.isAPIEnabled} onChange={v => handleChange("isAPIEnabled", v)} /></td></tr>
                    <tr><td>Live account</td><td><Switch value={configuration.isLiveAccount} onChange={v => handleChange("isLiveAccount", v)} /></td></tr>
                    <tr><td>Vytvářet objednávky</td><td><Switch value={configuration.allowPlaceOrder} onChange={v => handleChange("allowPlaceOrder", v)} /></td></tr>
                    <tr><td>Odesílat maily s stat.</td><td><Switch value={configuration.sendEmailStatements} onChange={v => handleChange("sendEmailStatements", v)} /></td></tr>
                    <tr><td>Automatické při/od hlašování</td><td><Switch value={configuration.isAutomaticLoggingEnabled} onChange={v => handleChange("isAutomaticLoggingEnabled", v)} /></td></tr>
                    <tr><td>Přihlašování CRON</td><td><InputText value={configuration.loginScriptCron} onChange={v => handleChange("loginScriptCron", v)} /></td></tr>
                    <tr><td>Odhlašování CRON</td><td><InputText value={configuration.logoutScriptCron} onChange={v => handleChange("logoutScriptCron", v)} /></td></tr>
                    <tr><td>Data script</td><td><Switch value={configuration.isDataScriptEnabled} onChange={v => handleChange("isDataScriptEnabled", v)} /></td></tr>
                    <tr><td>Data script CRON</td><td><InputText value={configuration.dataScriptCron} onChange={v => handleChange("dataScriptCron", v)} /></td></tr>
                    <tr><td>Predict script</td><td><Switch value={configuration.isPredictScriptEnabled} onChange={v => handleChange("isPredictScriptEnabled", v)} /></td></tr>
                    <tr><td>Predict script CRON</td><td><InputText value={configuration.predictScriptCron} onChange={v => handleChange("predictScriptCron", v)} /></td></tr>
                    <tr><td>Flow script</td><td><Switch value={configuration.isFlowScriptEnabled} onChange={v => handleChange("isFlowScriptEnabled", v)} /></td></tr>
                    <tr><td>Flow script CRON</td><td><InputText value={configuration.flowScriptCron} onChange={v => handleChange("flowScriptCron", v)} /></td></tr>

                    <tr><td>Strategy Bottom Up</td><td><Switch value={configuration.isStrategyBottomUpEnabled} onChange={v => handleChange("isStrategyBottomUpEnabled", v)} /></td></tr>
                    <tr><td>Bottom Up CRON</td><td><InputText value={configuration.strategyBottomUpCron} onChange={v => handleChange("strategyBottomUpCron", v)} /></td></tr>

                    <tr><td>Hlavní zdroj dat</td><td>
                        <select value={configuration.activeDataSource} onChange={e => handleChange("activeDataSource", e.target.value)}>
                            <option value="alpaca">Alpaca</option>
                            <option value="poly">Polygon</option>
                            <option value="ibkr">IBKR</option>
                        </select>
                    </td></tr>
                    <tr><td>Exchange</td><td>
                        <select value={configuration.dataSourceExchange} onChange={e => handleChange("dataSourceExchange", e.target.value)}>
                            {["NASDAQ", "IEX", "BATS", "BYX", "EDGX", "EDGEA"].map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                    </td></tr>
                    <tr><td>Limitní cena objednávky</td><td>
                        <input
                            style={{ display: "inline-block", width: "100px", marginRight: "10px", padding: "2px 4px" }}
                            type="number"
                            value={orderPriceLimit}
                            onChange={e => setOrderPriceLimit(Number((e.target as any).value))}
                            onBlur={e => handleChange("orderPriceLimit", Number((e.target as any).value))}
                        />
                        $
                    </td></tr>
                    <tr><td>Spread limit</td><td>
                        <input
                            style={{ display: "inline-block", width: "100px", marginRight: "10px", padding: "2px 4px" }}
                            type="number" min="0.01" max="100" step="0.01"
                            value={spreadLimit}
                            onChange={e => setSpreadLimit(Number((e.target as any).value))}
                            onBlur={e => handleChange("spreadLimitPercent", Number((e.target as any).value / 100))}
                        />
                        %
                    </td></tr>
                </tbody>
            </table>
        </>}
    </>;
}

export function InputText({ value, onChange }: { value: string, onChange: (value: string) => void }) {
    const [text, setText] = useState(value);

    useEffect(() => {
        setText(value);
    }, [value]);

    return <input type="text" value={text} onChange={e => setText(e.target.value)} onBlur={() => onChange(text)} />;
}

export function Switch({ value, onChange }: { value: boolean, onChange: (value: boolean) => void }) {
    return <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
            style={{ paddingLeft: "2px", paddingRight: "2px", display: "flex", alignItems: "center", justifyContent: value ? "flex-end" : "flex-start", cursor: "pointer", width: "40px", height: "24px", borderRadius: "12px", backgroundColor: value ? "red" : "gray" }}
            onClick={() => onChange(!value)}
        >
            <div style={{ width: "20px", height: "20px", borderRadius: "10px", backgroundColor: "white" }} />
        </div>
        {value ? "Enabled" : "Disabled"}
    </div>;
}

export function Performance() {

    const [performance, , isLoading, , reload] = useApi.getIBKRAccountPerformance({ period: "1M" });

    const [y, setY] = useState("diff");

    const chartData = useMemo(() => {
        let data: { date: string, value: number }[] = [];

        if (!performance) return data;

        if (y === "diff") {
            for (let i = 0; i < performance.nav.length; i++) {
                const v1 = i > 0 ? performance.nav[i - 1].value : performance.nav[i].value;
                const v2 = performance.nav[i].value;

                data.push({
                    date: formatDay(performance.nav[i].date),
                    value: v2 - v1,
                });
            }
        } else {
            data = performance[y as keyof AccountPerformance].map(({ date, value }) => ({ date: formatDay(date), value }));
        }

        return data;
    }, [performance, y]);

    function renderLabel({ x, y, value }: { x: number, y: number, value: number }) {

        return <g>
            <text x={x} y={y - 10} fontSize={15} fill={value > 0 ? colors.green : colors.red} textAnchor="middle">
                {getUsd(value)}
            </text>
        </g>
    }


    return <>
        {isLoading && <Loading />}
        {performance && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <div className="buttons">
                <button className={y === "nav" ? "active" : ""} onClick={() => setY("nav")}>Hodnota účtu</button>
                <button className={y === "diff" ? "active" : ""} onClick={() => setY("diff")}>Profit za den</button>
            </div>

            <AreaChart
                width={window.innerWidth - 80} // TODO: change with windows resize
                height={300}
                data={chartData}
                margin={{ left: 10, bottom: 20, right: 50, top: 25 }}
            >
                <XAxis dataKey="date" type="category">
                    <Label dy={25} value="Den" />
                </XAxis>
                <YAxis type="number">
                    <Label dx={-30} angle={270} value={"USD"} />
                </YAxis>
                <Tooltip content={({ active, payload, label }) => active && payload && payload.length > 0 && <div className="tooltip bg padding shadow">
                    <USD value={Number(payload[0]?.value)} />
                    <div>{label}</div>
                </div>} />

                <defs>
                    <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={colors.green} stopOpacity={1} />
                        <stop offset="100%" stopColor={colors.green} stopOpacity={0.2} />
                    </linearGradient>
                </defs>
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke={colors.neutral}
                    fill="url(#performanceGradient)"
                    dot={{ r: 5 }}
                >
                    <LabelList dataKey="value" position="top" content={renderLabel} />
                </Area>
            </AreaChart>
        </>}
    </>;
}

interface PerformanceRectProps {
    x: number;
    yBaseline: number;
    yValue: number;
    width: number;
    value: number;
    threshold: number;
    date: string;
}

export function PerformanceRect({
    x,
    yBaseline,
    yValue,
    width,
    value,
    threshold,
    date
}: PerformanceRectProps) {
    const isAbove = value >= threshold;
    const height = Math.abs(yBaseline - yValue);
    const y = isAbove ? yValue : yBaseline;
    const fill = isAbove ? colors.green : colors.red;
    const [isHovered, setIsHovered] = useState(false);

    return (
        <g
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ cursor: "pointer" }}
        >
            {/* Background highlight on hover */}
            {isHovered && (
                <rect
                    x={x - 4}
                    y={0}
                    width={width + 8}
                    height="100%"
                    fill="rgba(255, 255, 255, 0.05)"
                    rx={4}
                />
            )}

            {/* The actual bar */}
            <rect
                x={x}
                y={y}
                width={width}
                height={Math.max(height, 2)} // ensure at least 2px height so it is visible even if exactly at threshold
                fill={fill}
                rx={2}
                style={{
                    transition: "all 0.2s ease",
                    opacity: isHovered ? 1.0 : 0.8,
                }}
            />

            {/* Value text above/below the bar */}
            <text
                x={x + width / 2}
                y={isAbove ? y - 6 : y + height + 14}
                fill={isAbove ? "#a3e635" : "#fca5a5"} // slightly lighter version for readability
                fontSize={9}
                fontWeight="bold"
                textAnchor="middle"
                style={{ opacity: isHovered ? 1 : 0.7 }}
            >
                {Math.round(value).toLocaleString() + " $"}
            </text>
        </g>
    );
}

export function PerformanceBarChart70k() {
    const [period, setPeriod] = useState<"7D" | "1M" | "YTD" | "1Y">("1M");
    const [performance, , isLoading, , reload] = useApi.getIBKRAccountPerformance({ period });

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const THRESHOLD = 70000;

    const navData = useMemo(() => {
        if (!performance?.nav) return [];
        return performance.nav.map(item => ({
            date: formatDay(item.date),
            value: item.value
        }));
    }, [performance]);

    const stats = useMemo(() => {
        if (navData.length === 0) return null;
        const values = navData.map(d => d.value);
        const current = values[values.length - 1];
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);

        const currentDiff = current - THRESHOLD;
        const currentPercent = (currentDiff / THRESHOLD) * 100;

        return {
            current,
            currentDiff,
            currentPercent,
            minVal,
            maxVal
        };
    }, [navData]);

    const svgHeight = 320;
    const padding = { top: 40, bottom: 40, left: 65, right: 20 };
    const chartWidth = Math.max(windowWidth - 140, 600);
    const chartHeight = svgHeight - padding.top - padding.bottom;

    const { yMin, yMax, yBaseline, valueToY } = useMemo(() => {
        if (navData.length === 0) {
            return { yMin: 60000, yMax: 80000, yBaseline: chartHeight / 2, valueToY: (v: number) => 0 };
        }
        const values = navData.map(d => d.value);
        const minVal = Math.min(THRESHOLD, ...values);
        const maxVal = Math.max(THRESHOLD, ...values);
        const range = maxVal - minVal;
        const pad = range * 0.15 || 2000; // 15% padding at top and bottom

        const yMin = minVal - pad;
        const yMax = maxVal + pad;

        const valueToY = (val: number) => {
            const ratio = (val - yMin) / (yMax - yMin);
            return padding.top + (1 - ratio) * chartHeight;
        };

        const yBaseline = valueToY(THRESHOLD);

        return { yMin, yMax, yBaseline, valueToY };
    }, [navData, chartHeight]);

    if (isLoading) return <Loading />;
    if (!performance) return <p>Nepodařilo se načíst data o výkonu.</p>;
    if (navData.length === 0) return <p>Žádná data pro vybrané období.</p>;

    const count = navData.length;
    const slotWidth = chartWidth / count;
    const barWidth = Math.max(slotWidth * 0.5, 6);

    return (
        <div style={{ padding: "10px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", flexWrap: "wrap", gap: "10px" }}>
                <div className="buttons">
                    {(["7D", "1M", "YTD", "1Y"] as const).map(p => (
                        <button
                            key={p}
                            className={period === p ? "active" : ""}
                            onClick={() => setPeriod(p)}
                        >
                            {p === "7D" ? "7 Dní" : p === "1M" ? "1 Měsíc" : p === "YTD" ? "YTD" : "1 Rok"}
                        </button>
                    ))}
                </div>
                <button onClick={reload}>Přenačíst</button>
            </div>

            {stats && (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "15px",
                    marginBottom: "20px"
                }}>
                    <div style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", border: "1px solid #333" }}>
                        <span style={{ fontSize: "12px", color: colors.neutral, display: "block" }}>Aktuální hodnota</span>
                        <span style={{ fontSize: "18px", fontWeight: "bold" }}>{Math.round(stats.current).toLocaleString()} USD</span>
                        <span style={{
                            fontSize: "12px",
                            display: "block",
                            color: stats.currentDiff >= 0 ? colors.green : colors.red,
                            fontWeight: "bold",
                            marginTop: "2px"
                        }}>
                            {stats.currentDiff >= 0 ? "+" : ""}{Math.round(stats.currentDiff).toLocaleString()} USD ({stats.currentPercent.toFixed(2)}% k 70k)
                        </span>
                    </div>
                    <div style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", border: "1px solid #333" }}>
                        <span style={{ fontSize: "12px", color: colors.neutral, display: "block" }}>Minimum v období</span>
                        <span style={{ fontSize: "18px", fontWeight: "bold" }}>{Math.round(stats.minVal).toLocaleString()} USD</span>
                        <span style={{
                            fontSize: "12px",
                            display: "block",
                            color: (stats.minVal - THRESHOLD) >= 0 ? colors.green : colors.red,
                            marginTop: "2px"
                        }}>
                            {Math.round(stats.minVal - THRESHOLD).toLocaleString()} USD
                        </span>
                    </div>
                    <div style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", border: "1px solid #333" }}>
                        <span style={{ fontSize: "12px", color: colors.neutral, display: "block" }}>Maximum v období</span>
                        <span style={{ fontSize: "18px", fontWeight: "bold" }}>{Math.round(stats.maxVal).toLocaleString()} USD</span>
                        <span style={{
                            fontSize: "12px",
                            display: "block",
                            color: (stats.maxVal - THRESHOLD) >= 0 ? colors.green : colors.red,
                            marginTop: "2px"
                        }}>
                            +{Math.round(stats.maxVal - THRESHOLD).toLocaleString()} USD
                        </span>
                    </div>
                    {period === "1M" && (
                        <div style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", border: "1px solid #333" }}>
                            <span style={{ fontSize: "12px", color: colors.neutral, display: "block" }}>Projekce na rok (p.a.)</span>
                            <span style={{ fontSize: "18px", fontWeight: "bold", color: stats.currentDiff >= 0 ? colors.green : colors.red }}>
                                {stats.currentPercent >= 0 ? "+" : ""}{(stats.currentPercent * 12).toFixed(2)}% p.a.
                            </span>
                            <span style={{
                                fontSize: "12px",
                                display: "block",
                                color: colors.neutral,
                                marginTop: "2px"
                            }}>
                                ({stats.currentDiff >= 0 ? "+" : ""}{Math.round(stats.currentDiff * 12).toLocaleString()} USD / rok)
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div style={{ overflowX: "auto", background: "#11140e", borderRadius: "10px", padding: "15px 10px", border: "1px solid #222" }}>
                <svg width={chartWidth + padding.left + padding.right} height={svgHeight}>
                    {/* Y Axis Guide Lines */}
                    <line
                        x1={padding.left}
                        y1={padding.top}
                        x2={chartWidth + padding.left}
                        y2={padding.top}
                        stroke="#222"
                        strokeWidth={1}
                    />
                    <line
                        x1={padding.left}
                        y1={svgHeight - padding.bottom}
                        x2={chartWidth + padding.left}
                        y2={svgHeight - padding.bottom}
                        stroke="#222"
                        strokeWidth={1}
                    />

                    {/* Y Axis Labels */}
                    <text x={padding.left - 10} y={padding.top + 4} fill={colors.neutral} fontSize={10} textAnchor="end">
                        {Math.round(yMax).toLocaleString()} USD
                    </text>
                    <text x={padding.left - 10} y={svgHeight - padding.bottom + 4} fill={colors.neutral} fontSize={10} textAnchor="end">
                        {Math.round(yMin).toLocaleString()} USD
                    </text>

                    {/* 70,000 Reference Line */}
                    <line
                        x1={padding.left}
                        y1={yBaseline}
                        x2={chartWidth + padding.left}
                        y2={yBaseline}
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth={1.5}
                        strokeDasharray="4,4"
                    />
                    <text
                        x={padding.left - 10}
                        y={yBaseline + 4}
                        fill="#fff"
                        fontSize={11}
                        fontWeight="bold"
                        textAnchor="end"
                    >
                        70 000
                    </text>

                    {/* Bars */}
                    {navData.map((d, index) => {
                        const x = padding.left + index * slotWidth + (slotWidth - barWidth) / 2;
                        const yValue = valueToY(d.value);

                        // We only show date labels if they don't crowd too much.
                        // For 7D, show all. For 1M, show every 3rd. For YTD/1Y, show every 10th or so.
                        let showDateLabel = false;
                        if (period === "7D") showDateLabel = true;
                        else if (period === "1M" && index % 3 === 0) showDateLabel = true;
                        else if (period === "YTD" && index % 10 === 0) showDateLabel = true;
                        else if (period === "1Y" && index % 20 === 0) showDateLabel = true;

                        return (
                            <g key={index}>
                                <PerformanceRect
                                    x={x}
                                    yBaseline={yBaseline}
                                    yValue={yValue}
                                    width={barWidth}
                                    value={d.value}
                                    threshold={THRESHOLD}
                                    date={d.date}
                                />

                                {showDateLabel && (
                                    <text
                                        x={x + barWidth / 2}
                                        y={svgHeight - padding.bottom + 18}
                                        fill={colors.neutral}
                                        fontSize={9}
                                        textAnchor="middle"
                                    >
                                        {d.date.substring(5)} {/* show MM-DD or similar */}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
}

export function Ledger() {
    const [ledger, , isLoading, , reload] = useApi.getIBKRPortfolioLedger({});

    return <>
        {isLoading && <Loading />}
        {ledger && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <p className="left">K datu: {formatDateTime(ledger.BASE.timestamp)}</p>
            <TableInline
                rows={Object.values(ledger)}
                columns={[
                    // { title: "Aktualizováno", key: "timestamp", render: "datetime" },
                    { title: "Měna", key: "currency" },
                    { title: "Likvidační zůstatek", key: "netliquidationvalue", render: "usd" },
                    { title: "Hotovost", key: "cashbalance", render: "usd" },
                    { title: "Akcie", key: "stockmarketvalue", render: "usd" },
                    { title: "Futures", key: "futuremarketvalue", render: "usd" },
                    { title: "Nerealizovaná PnL", key: "unrealizedpnl", render: "usd" },
                    { title: "Realizovaná PnL", key: "realizedpnl", render: "usd" },
                ]}
            />
        </>}
    </>;
}

export function Positions() {
    const [positions, , isLoading, , reload] = useApi.getIBKRPositions({});

    return <>
        {isLoading && <Loading />}
        {positions && <>
            <button className="right" onClick={reload}>Přenačíst</button>

            {positions.length > 0 && <p className="left">
                K datu: {formatDateTime(positions[0].timestamp)}
                <br />Neuskutečněný PnL: <USD value={ss.sum(positions.map(p => p.unrealizedPnl))} />
                <br />Uskutečněný PnL: <USD value={ss.sum(positions.map(p => p.realizedPnl))} />
            </p>}
            <TableInline
                rows={positions.map(p => ({ ...p, pnlPercentage: p.unrealizedPnl / (p.position * p.avgPrice) }))}
                columns={[
                    // { title: "Aktualizováno", key: "timestamp", render: "datetime" },
                    // { title: "ConId", key: "conid" },
                    // { title: "Asset", key: "assetClass", render: "string" },
                    { title: "Symbol", key: "description", render: "string" },
                    { title: "Množství", key: "position", render: "number" },
                    { title: "Nákupní hodnota (AVG)", key: "avgCost", render: "usd" },
                    { title: "Aktuální hodnota", key: "marketPrice", render: "usd" },
                    { title: "Celková hodnota", key: "marketValue", render: "usd" },
                    { title: "Neuskutečněný PnL", key: "unrealizedPnl", render: "usd" },
                    { title: "%", key: "pnlPercentage", render: "percent" },
                    { title: "Uskutečněný PnL", key: "realizedPnl", render: "usd" },
                ]}
            />
        </>}
    </>;
}

function Select({ options, value, onChange }: { options: string[], value: string, onChange: (value: string) => void }) {

    return <div className="select">
        <select value={value} onChange={e => onChange(e.target.value)}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    </div>;
}

export function ClosedTrades() {
    const [trades, , isLoading, , reload] = useApi.getIBKRClosedTrades({});

    const [day, setDay] = useState("");

    const days = uniq((trades || []).map(t => formatDay(t.dateSell))).sort();
    const dayTrades = (trades || []).filter(t => formatDay(t.dateSell) === day);

    useEffect(() => {
        if (trades && days.length > 0)
            setDay(days[days.length - 1]);
    }, [trades]);


    return <>
        {isLoading && <Loading />}
        {trades && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <Select options={days} value={day} onChange={setDay} />

            {dayTrades.length > 0 && <p className="left">
                Profit: <USD value={ss.sum(dayTrades.map(t => t.gainPrice))} />
                <br />Komise: <USD value={ss.sum(dayTrades.map(t => t.commission))} />
                <br /><b>Celkem: <USD value={ss.sum(dayTrades.map(t => t.gainPrice - t.commission))} /></b>
            </p>}

            <TableInline
                rows={dayTrades}
                columns={[
                    { title: "Symbol", key: "symbol", render: "string" },
                    { title: "Qty", key: "qty", render: "number" },
                    { title: "Date buy", key: "dateBuy", render: "datetime" },
                    { title: "Date sell", key: "dateSell", render: "datetime" },
                    { title: "Buy price", key: "buyPrice", render: "usd" },
                    { title: "Sell price", key: "sellPrice", render: "usd" },
                    { title: "Profit", key: "gainPrice", render: "usd" },
                    { title: "Komise", key: "commission", render: "usd" },
                ]}
            />
            <p>* Aktualizuji jednou za hodinu</p>
        </>}
    </>;
}

export function Trades() {
    const [trades, , isLoading, , reload] = useApi.getIBKRTrades({ days: 30 });

    const [day, setDay] = useState("");

    const days = uniq((trades || []).map(t => formatDay(getDateTime(t.trade_time_r)))).sort();
    const dayTrades = (trades || []).filter(t => formatDay(getDateTime(t.trade_time_r)) === day);

    useEffect(() => {
        if (trades && days.length > 0)
            setDay(days[days.length - 1]);
    }, [trades]);

    return <>
        {isLoading && <Loading />}
        {trades && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <Select options={days} value={day} onChange={setDay} />

            {dayTrades.length > 0 && <p className="left">
                Nakoupeno za: <USD value={ss.sum(dayTrades.filter(t => t.side === "B").map(t => -t.net_amount))} />
                <br />Prodáno za: <USD value={ss.sum(dayTrades.filter(t => t.side === "S").map(t => t.net_amount))} />
                <br /><b>Komise:</b> <USD value={ss.sum(dayTrades.map(t => -Number(t.commission || 0)))} />
                <br /><b>Celkem:</b> <USD value={ss.sum(dayTrades.map(t => (t.side === "B" ? -t.net_amount : t.net_amount) - Number(t.commission || 0)))} />
            </p>}

            <TableInline
                rows={dayTrades}
                columns={[
                    { title: "Kdy", render: (r) => formatDateTime(getDateTime(r.trade_time_r)) },
                    // { title: "ConId", key: "conid", render: "string" },
                    // { title: "Order ID", key: "order_id", render: "string" },
                    { title: "Symbol", key: "symbol" },
                    { title: "Qty", key: "size", render: "number" },
                    // { title: "Exchange", key: "exchange", render: "string" },
                    { title: "Buy / Sell", key: "side", render: "string" },
                    { title: "Cena", key: "price", render: "usd" },
                    { title: "Cena celkově", key: "net_amount", render: "usd" },
                    { title: "Komise", key: "commission", render: "usd" },
                ]}
            />
            <p>* Aktualizuji jednou za hodinu</p>
        </>}
    </>;
}

export function Orders() {
    const [orders, , isLoading, , reload] = useApi.getIBKROrders({});

    const [day, setDay] = useState("");

    const days = uniq((orders || []).map(o => formatDay(o.date))).sort();

    useEffect(() => {
        if (orders && days.length > 0)
            setDay(days[days.length - 1]);
    }, [orders]);

    const data = useMemo(() => {
        if (!orders) return [];

        return orders
            .filter(o => formatDay(o.date) === day)
            .map(({ date, trade, orders, orderError }) => ({
                date,
                qty: orders[0]?.quantity || 0,
                open: ss.average([trade.openLow, trade.openHigh]),
                ...trade,
                orderError,
            }));
    }, [orders, day]);

    return <>
        {isLoading && <Loading />}
        {orders && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <Select options={days} value={day} onChange={setDay} />

            <p className="left">
                Prošlo: {data.filter(f => !f.orderError).length}
                <br />S chybou: {data.filter(f => f.orderError).length}
                <br />Celkem: {data.length}
            </p>
            <TableInline
                rows={data}
                columns={[
                    { title: "Kdy", key: "date", render: "datetime" },
                    { title: "Strategie", key: "strategyName" },
                    { title: "Symbol", key: "symbol" },
                    { title: "Qty", key: "qty", render: "number" },
                    { title: "Open", key: "open", render: "usd" },
                    { title: "Take profit", key: "takeProfit", render: "usd" },
                    { title: "Stop loss", key: "stopLoss", render: "usd" },
                    { title: "Bid-Ask spread", key: "bidAskSpread", render: "usd" },
                    { title: "TP %", key: "takeProfitPercent", render: "percent" },
                    { title: "SL %", key: "stopLossPercent", render: "percent" },
                    { title: "Spread %", key: "bidAskSpreadPercent", render: "percent" },
                    { title: "Chyba", key: "orderError", render: "json" },
                ]}
            />
        </>}
    </>;
}


export function LiveOrders() {
    const [orders, , isLoading, , reload] = useApi.getIBKRLiveOrders({});

    return <>
        {isLoading && <Loading />}
        {orders && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <p><b>{orders.snapshot ? "Snapshot 🙁" : "Live 😊"}</b></p>
            <TableInline
                rows={sortBy(orders.orders, ["status", "lastExecutionTime_r"])}
                columns={[
                    { title: "Kdy", render: (r) => formatDateTime(getDateTime(r.lastExecutionTime_r)) },
                    // { title: "ConId", key: "conid", render: "string" },
                    { title: "Order ID", key: "orderId", render: "string" },
                    { title: "Symbol", key: "ticker" },
                    { title: "Buy / Sell", key: "side", render: "string" },
                    { title: "Qty", key: "totalSize", render: "number" },
                    // { title: "Exchange", key: "listingExchange", render: "string" },
                    { title: "Status", key: "status", render: "string" },
                    { title: "Order type", key: "origOrderType", render: "string" },
                    { title: "Cena", render: r => <USD value={Number(r.price || r.stop_price || r.avgPrice)} /> },
                    // { title: "Cena", key: "price" },
                    // { title: "Cena Stop", key: "stop_price" },
                    // { title: "Cena AVG", key: "avgPrice" },
                    { title: "Typ", key: "timeInForce", render: "string" },
                    { title: "Reference", key: "order_ref" },
                ]}
            />
        </>}
    </>;
}

export function Logs() {
    const [logs, , isLoading, , reload] = useApi.getIBKRLogs({});

    return <>
        {isLoading && <Loading />}
        {logs && <>
            <button className="right" onClick={reload}>Přenačíst</button>
            <TableInline
                rows={logs}
                columns={[
                    { title: "Kdy", key: "dateTime", render: "datetime" },
                    { title: "Metoda", key: "method" },
                    { title: "URL", key: "url" },
                    { title: "Paramtery", key: "params", render: "json" },
                    { title: "Status", key: "status", render: "number" },
                    { title: "Chyba", key: "error", render: "json" },
                ]}
            />
        </>}
    </>;
}

// -----------------------------------  MAIL  ----------------------------------
export function StatementsTableMail({ account, portfolio }: { account: Account, portfolio: PortfolioSummarySimple }) {
    const itemPairs = [
        [
            { label: "Účet", value: account.selectedAccount },
            { label: "Typ", value: account.isPaper ? <span style={{ color: "#16a34a", fontWeight: "bold" }}>🟢 Paper Trading</span> : <span style={{ color: "#dc2626", fontWeight: "bold" }}>🔴 Live Trading</span> },
        ],
        [
            { label: "Celková hodnota účtu", value: <strong style={{ color: "#0f172a", fontSize: "14px" }}><USD value={portfolio.netliquidation} /></strong> },
            { label: "Celková hotovost", value: <strong style={{ color: "#0f172a" }}><USD value={portfolio.totalcashvalue} /></strong> },
        ],
        [
            { label: "Nákupní síla", value: <USD value={portfolio.buyingpower} /> },
            { label: "Dostupné prostředky", value: <USD value={portfolio.availablefunds} /> },
        ],
        [
            { label: "Přebytečná likvidita ⚠️", value: <USD value={portfolio.excessliquidity} /> },
            { label: "Udržovací marže", value: <USD value={portfolio.maintmarginreq} /> },
        ],
        [
            { label: "Celkem investováno", value: <USD value={portfolio.grosspositionvalue} /> },
            { label: "Páka", value: portfolio["leverage-s"] },
        ],
        [
            { label: "Polštář", value: <Percent value={Number(portfolio.cushion)} /> },
            { label: "Aktualizováno", value: formatDateTime(portfolio.dateTime) },
        ],
    ];

    return (
        <div style={{ width: "100%", borderRadius: "10px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", overflow: "hidden", margin: "10px 0 20px 0" }}>
            <table cellPadding="0" cellSpacing="0" style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, sans-serif" }}>
                <tbody>
                    {itemPairs.map((pair, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 1 ? "#f8fafc" : "#ffffff", borderBottom: index < itemPairs.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                            <td style={{ width: "50%", padding: "10px 14px", borderRight: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                                <table cellPadding="0" cellSpacing="0" style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ color: "#64748b", fontSize: "12px", fontWeight: 500 }}>{pair[0].label}</td>
                                            <td style={{ color: "#1e293b", fontSize: "13px", fontWeight: 600, textAlign: "right" }}>{pair[0].value}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ width: "50%", padding: "10px 14px", verticalAlign: "middle" }}>
                                {pair[1] && (
                                    <table cellPadding="0" cellSpacing="0" style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ color: "#64748b", fontSize: "12px", fontWeight: 500 }}>{pair[1].label}</td>
                                                <td style={{ color: "#1e293b", fontSize: "13px", fontWeight: 600, textAlign: "right" }}>{pair[1].value}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function TradesTableMail({ orders }: { orders: TradeType[] }) {
    return <>
        <TableMail
            rows={orders}
            columns={[
                { title: "Symbol", key: "symbol" },
                { title: "Open", key: "openHigh", render: "usd" },
                { title: "Take profit", key: "takeProfitPercent", render: "percent" },
                { title: "Stop loss", key: "stopLossPercent", render: "percent" },
                { title: "Bid-Ask spread", key: "bidAskSpread", render: "usd" },
                { title: "Spread %", key: "bidAskSpreadPercent", render: "percent" },
                { title: "Is Quote", key: "isQuote" },
                { title: "Is Spread", key: "isBidAskSpread" },
                { title: "Is small spread", key: "isEnoughSpread" },
            ]}
        />
    </>;
}

export function CreateOrderTableMail({ orders }: { orders: IBKROrder[] }) {

    const ordersMap = orders.map(({ date, trade, orderError, orders }) => ({
        date,
        qty: orders[0].quantity,
        open: ss.average([trade.openLow, trade.openHigh]),
        orderError,
        ...trade,
    }));

    return <>
        <TableMail
            rows={ordersMap}
            columns={[
                { title: "Date", key: "date", render: "datetime" },
                { title: "Symbol", key: "symbol" },
                { title: "Open", key: "open", render: "usd" },
                { title: "Take profit", key: "takeProfitPercent", render: "percent" },
                { title: "Stop loss", key: "stopLossPercent", render: "percent" },
                { title: "Bid-Ask spread", key: "bidAskSpread", render: "usd" },
                { title: "Spread %", key: "bidAskSpreadPercent", render: "percent" },
                { title: "Qty", key: "qty", render: "number" },
                { title: "Error", key: "orderError", render: "json" },
            ]}
        />
    </>;
}